import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Chip,
  StyledIcon,
  ErrorMessage,
  Container,
  TextFieldContainer,
} from "./MultiValueTextField.ui";
import { BaseComponentProps } from "../types";
import { TextField } from "../TextField";

export enum MultiValueTextFieldTestIds {
  Component = "multi-value-text-field",
}

export enum ValidationType {
  Email = "email",
}

const regexByValidationType: {
  [key in ValidationType]: RegExp;
} = {
  [ValidationType.Email]: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
};

export type Chip = {
  label: string;
  value: string;
};

type MultiValueTextFieldProps = Omit<BaseComponentProps<"input">, "value"> & {
  id?: string;
  className?: string;
  testId?: string;
  placeholder?: string;
  errorMessage?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValidate?: (isValid: boolean) => void;
  value?: Chip[];
  onAddChip?: (chip: Chip) => void;
  onCloseChip?: (chip: Chip) => void;
  validation?: ValidationType;
  customValidation?: RegExp;
};

export const MultiValueTextField: FC<MultiValueTextFieldProps> = ({
  id,
  className,
  testId,
  placeholder,
  value: controlledValue,
  onAddChip,
  onCloseChip,
  onChange,
  onValidate,
  errorMessage = "Invalid input.",
  validation,
  customValidation,
  ...inputProps
}) => {
  const [currentText, setCurrentText] = useState("");
  const [value, setValue] = useState(controlledValue || []);
  const [isValid, setIsValid] = useState(true);
  const [invalidChips, setInvalidChips] = useState<Chip[]>([]);

  const isControlledComponent = controlledValue !== undefined;

  useEffect(() => {
    setValue(controlledValue || []);
  }, [controlledValue]);

  // kept in a ref so the validation callback stays stable when consumers pass
  // a new onValidate on every render
  const onValidateRef = useRef(onValidate);
  onValidateRef.current = onValidate;

  // `undefined` until the first run, so mount still notifies exactly once, as it
  // did when this effect only depended on `value`
  const lastNotifiedValidityRef = useRef<boolean | undefined>(undefined);

  const isValidInputText = useCallback(
    (text: string): boolean => {
      let isValidInputText = true;

      if (validation) {
        const myRegex: RegExp = regexByValidationType[validation];
        isValidInputText = isValidInputText && myRegex.test(text);
      }
      if (isValidInputText && customValidation) {
        isValidInputText = isValidInputText && customValidation.test(text);
      }

      return isValidInputText;
    },
    [validation, customValidation],
  );

  const handleValidation = useCallback(() => {
    const invalidChips = value.filter((chip) => !isValidInputText(chip.value));
    const isValidValue = invalidChips?.length <= 0;

    setIsValid(isValidValue);
    // `invalidChips` is a fresh array on every run, so it would never match the
    // previous state and would re-render on each parent render. Consumers pass
    // `customValidation` as an inline regex, which rebuilds this callback, so
    // keep the old array when the invalid chips are the same.
    setInvalidChips((previousInvalidChips) =>
      previousInvalidChips.length === invalidChips.length &&
      previousInvalidChips.every(
        (chip, index) => chip.value === invalidChips[index].value,
      )
        ? previousInvalidChips
        : invalidChips,
    );
    // only notify on a real change: the effect re-runs whenever the memoised
    // callback is rebuilt, which happens on every parent render when
    // `customValidation` is passed as an inline regex
    if (lastNotifiedValidityRef.current !== isValidValue) {
      lastNotifiedValidityRef.current = isValidValue;
      onValidateRef.current?.(isValidValue);
    }
  }, [value, isValidInputText]);

  useEffect(() => {
    handleValidation();
  }, [handleValidation]);

  const createNewChip = (chip: Chip) => {
    if (!isControlledComponent) {
      setValue([...value, chip]);
    }
    onAddChip?.(chip);
    setCurrentText("");
  };

  /**
   * unlike with handleOnKeyDown, we're calling createNewChip
   * everytime the blur event is called, regardless of if the
   * component is being used in a controlled manner. we should
   * confirm later if handleOnKeyDown should follow the same
   * behavior or not
   */
  const handleOnBlur = () => {
    if (currentText) {
      createNewChip({ label: currentText, value: currentText });
    }
  };

  const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      currentText &&
      (event.code === "Enter" ||
        event.code === "Space" ||
        event.code === "Comma")
    ) {
      const newChip: Chip = {
        label: currentText,
        value: currentText,
      };
      createNewChip(newChip);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ",") {
      setCurrentText(event.target.value.trim());
      onChange?.(event);
    }
  };

  const handleCloseChip = (chipToDelete: Chip) => {
    if (!isControlledComponent) {
      setValue(value.filter((chip) => chip.value !== chipToDelete.value));
    }

    onCloseChip?.(chipToDelete);
  };

  return (
    <Container
      id={id}
      className={className}
      data-testid={testId ?? MultiValueTextFieldTestIds.Component}
    >
      <TextFieldContainer className={isValid ? "" : "danger"}>
        {value.map((chip, i) => (
          <Chip
            key={`${chip.value}-${i}`}
            className={
              invalidChips.find(
                (invalidChip) => invalidChip.value === chip.value,
              )
                ? "danger"
                : ""
            }
          >
            {chip.label}
            <StyledIcon
              iconName="x-close"
              iconSize="small"
              plain
              onClick={(): void => handleCloseChip(chip)}
            />
          </Chip>
        ))}
        <TextField
          value={currentText}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          placeholder={placeholder}
          {...inputProps}
        />
      </TextFieldContainer>
      {!isValid && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Container>
  );
};
